type WhatsAppBrandingSettings = {
  logoUrl?: string;
  printSettings?: {
    prescriptionHeader?: {
      centerBadgeBase64?: string;
      centerBadgeSize?: number;
      centerBadgeWidth?: number;
      centerBadgeHeight?: number;
    };
  };
};

const WHATSAPP_LOGO_SCALE = 2;

/**
 * Add the configured prescription badge to HTML uploaded for WhatsApp only.
 * WhatsApp links use a full browser viewport rather than a scaled paper
 * preview, so render the saved badge size at 2x for comparable visibility.
 * Printed documents keep their existing markup and layout.
 */
export function applyWhatsAppLogo(html: string, settings?: WhatsAppBrandingSettings): string {
  if (!html || !settings) return html;

  const header = settings.printSettings?.prescriptionHeader;
  const logoSrc = String(header?.centerBadgeBase64 || settings.logoUrl || '').trim();
  if (!logoSrc) return html;

  const configuredWidth = Number(header?.centerBadgeWidth || header?.centerBadgeSize);
  const configuredHeight = Number(header?.centerBadgeHeight);
  const settingsLogoWidth = Number.isFinite(configuredWidth) && configuredWidth > 0 ? configuredWidth : 96;
  const logoWidth = settingsLogoWidth * WHATSAPP_LOGO_SCALE;
  const logoHeight = Number.isFinite(configuredHeight) && configuredHeight > 0 ? configuredHeight * WHATSAPP_LOGO_SCALE : 0;
  const logoHeightStyle = logoHeight ? `height:${logoHeight}px;` : 'height:auto;';

  // Prescription HTML already contains the badge. Keep it in place and make
  // its width at the WhatsApp display scale derived from Settings.
  if (/class=["'][^"']*rx-col-center[^"']*["'][^>]*>\s*<img/i.test(html)) {
    return html.replace(
      /(<div\s+class=["'][^"']*rx-col-center[^"']*["'][^>]*>\s*<img\b[^>]*?style=["'])([^"']*)(["'])/i,
      (_match, start: string, style: string, quote: string) => {
        const withoutWidth = style.replace(/(?:^|;)\s*width\s*:[^;]*/i, '');
        const withoutHeight = withoutWidth.replace(/(?:^|;)\s*height\s*:[^;]*/i, '');
        return `${start}width:${logoWidth}px;${logoHeightStyle}max-width:60vw;${withoutHeight}${quote}`;
      }
    );
  }

  const safeSrc = logoSrc.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  const logo = `<div data-nw-whatsapp-logo="true" style="text-align:center;margin:0 auto 12px;width:100%"><img src="${safeSrc}" alt="" style="width:${logoWidth}px;max-width:60vw;${logoHeightStyle}display:inline-block;object-fit:contain" /></div>`;

  // Put the logo inside the existing document header/card so flex body
  // layouts remain unchanged.
  const containers = [
    /(<div\b[^>]*class=["'][^"']*clinic-header[^"']*["'][^>]*>)/i,
    /(<div\b[^>]*class=["'][^"']*header[^"']*["'][^>]*>)/i,
    /(<div\b[^>]*class=["'][^"']*card[^"']*["'][^>]*>)/i,
    /(<main\b[^>]*>)/i,
    /(<body\b[^>]*>)/i,
  ];

  for (const container of containers) {
    if (container.test(html)) return html.replace(container, `$1${logo}`);
  }

  return html;
}
